import React, { useState, useEffect } from 'react';
import { Image, Video, Download } from 'lucide-react';
import { contentService, eventsService } from '../services/api-service';
import './Gallery.css';

const Gallery = () => {
  const [filter, setFilter] = useState('all'); // all, images, videos
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [allGallery, setAllGallery] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gallery, events, flyersData] = await Promise.all([
        contentService.getAllGalleryItems(),
        eventsService.getAllEvents(),
        contentService.getAllFlyers()
      ]);
      
      setAllGallery(gallery);
      setAllEvents(events);
      setFlyers(flyersData);
    } catch (error) {
      console.error('Error loading gallery data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredGallery = () => {
    let items = allGallery;

    // Filter by type
    if (filter === 'images') {
      items = items.filter(item => item.type === 'image');
    } else if (filter === 'videos') {
      items = items.filter(item => item.type === 'video');
    }

    // Filter by event
    if (selectedEvent !== 'all') {
      items = items.filter(item => item.eventId === selectedEvent);
    }

    return items;
  };

  const filteredGallery = getFilteredGallery();

  return (
    <div className="gallery-page">
      {/* Header */}
      <section className="gallery-header">
        <div className="container">
          <div className="gallery-header-content">
            <Image size={48} className="gallery-header-icon" />
            <h1>Gallery</h1>
            <p>
              Memories from our celebrations - capturing the moments, energy, and 
              connections that make B2P special.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="gallery-filters">
        <div className="container">
          <div className="gallery-filters-group">
            <label>Type:</label>
            <div className="gallery-filters-buttons">
              <button
                className={`gallery-filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={`gallery-filter-btn ${filter === 'images' ? 'active' : ''}`}
                onClick={() => setFilter('images')}
              >
                <Image size={16} />
                Images
              </button>
              <button
                className={`gallery-filter-btn ${filter === 'videos' ? 'active' : ''}`}
                onClick={() => setFilter('videos')}
              >
                <Video size={16} />
                Videos
              </button>
            </div>
          </div>

          <div className="gallery-filters-group">
            <label>Event:</label>
            <select
              className="gallery-select"
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
            >
              <option value="all">All Events</option>
              {allEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="gallery-grid-section section">
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p>Loading gallery...</p>
            </div>
          ) : filteredGallery.length > 0 ? (
            <div className="gallery-grid">
              {filteredGallery.map((item) => (
                <div key={item.id} className="gallery-item">
                  {item.type === 'image' ? (
                    <img src={item.thumbnail || item.url} alt={item.caption} />
                  ) : (
                    <div className="gallery-video">
                      <video src={item.url} controls poster={item.thumbnail} />
                    </div>
                  )}
                  {item.caption && (
                    <div className="gallery-item-caption">
                      <p>{item.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="gallery-empty">
              <Image size={64} className="gallery-empty-icon" />
              <h3>No content found</h3>
              <p>Try adjusting your filters</p>
            </div>
          )}
        </div>
      </section>

      {/* Flyers Section */}
      {flyers.length > 0 && (
        <section className="gallery-flyers section bg-gray">
          <div className="container">
            <h2>Event Flyers</h2>
            <div className="gallery-flyers-grid">
              {flyers.map((flyer) => (
                <div key={flyer.id} className="gallery-flyer-card">
                  <img src={flyer.url} alt={flyer.title} />
                  <div className="gallery-flyer-content">
                    <h3>{flyer.title}</h3>
                    {flyer.downloadUrl && (
                      <a 
                        href={flyer.downloadUrl} 
                        download 
                        className="gallery-flyer-download"
                      >
                        <Download size={16} />
                        Download
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Gallery;


